import React, { ReactElement } from 'react';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Toolbar,
  Hidden,
  List,
  ListItem,
  Typography,
  IconButton,
  Container,
  Link,
  Box,
} from '@material-ui/core';

import { ButtonForward, Image } from 'src/components/atoms';
import { CustomTheme } from 'src/themes';

import SvgLogo from 'src/assets/logo.svg';
import SvgSecurity from 'src/assets/badges/security.svg';
import SvgPhone from 'src/assets/badges/phone.svg';
import SvgUser from 'src/assets/badges/user.svg';
import ButtonLogout from 'src/components/atoms/ButtonLogout';
import { useDispatch, useSelector } from 'react-redux';
import { IReduxState } from 'src/store/reducers';
import useDeviseQuery from 'src/hooks/useDeviseQuery';
import { URLS } from 'src/utils/consts';
import { logout } from 'src/store/actions';
import { RETAIL_CONTACT_MESSAGE, CONTACT_NUMBER } from 'src/utils/config';

interface ToolbarProps {
  onSidebarOpen?: () => void;
  showMenu?: boolean;
  showServiceWarranty?: boolean;
  unClickable?: boolean;
  isFleet?: boolean;
}

const useStyles = makeStyles((theme: CustomTheme) => ({
  contactBar: {
    width: '100%',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#2A2D3C',
  },
  contactInfoContainer: {
    width: '100%',
    margin: '0 auto',
    padding: 0,
    color: '#FFFFFF',
    display: 'flex',
    height: '100%',
    alignItems: 'center',

    fontFamily: 'Inter',
    fontSize: '13px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '18px',
    letterSpacing: '0px',
    textAlign: 'left',

    '& .button': {
      fontFamily: 'Inter',
      background: 'transparent',
      border: 'none',
      color: '#FFFFFF',
      cursor: 'pointer',
      textDecoration: 'none',
      fontSize: 13,
      fontWeight: 400,
    },
  },
  flexGrow: {
    flexGrow: 1,
  },
  buttonQuote: {
    width: '150px',
    borderRadius: '31px',
  },
  navigationContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  toolbar: {
    width: '100%',
    margin: '0 auto',
  },
  listItem: {
    cursor: 'pointer',
    paddingTop: 0,
    paddingBottom: 0,
  },
  listItemText: {
    flex: '0 0 auto',
    whiteSpace: 'nowrap',
    textDecoration: 'none',

    color: '#2A2D3C',
    fontFamily: 'Inter',
    fontSize: '15px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '25px',
    letterSpacing: '0px',
  },
  listItemButton: {
    whiteSpace: 'nowrap',
  },
  iconButton: {
    padding: '5px',
    '&:hover': {
      background: 'transparent',
    },
  },
  logoContainer: {
    height: 28,
    [theme.breakpoints.up('sm')]: {
      width: 120,
      minWidth: 120,
      height: 32,
    },
  },
  logoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  imageCenterAlign: {
    flexGrow: 1,
    textAlign: 'center',
  },
  contactImage1: {
    width: '20px',
    height: '20px',
    objectFit: 'contain',
    color: '#57FFC4',
    marginRight: '10px',
  },
  contactImage2: {
    width: '15px',
    height: '15px',
    objectFit: 'contain',
    color: '#57FFC4',
    marginRight: '10px',
    marginLeft: '30px',
    marginTop: '-2px',
  },
  containerPhone: {
    background: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imgServiceProvider: {
    width: 30,
    height: 30,
    marginRight: theme.spacing(1),
    borderRadius: 30,
  },
  titlePhone: {
    color: '#302A3C',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: 17,
    lineHeight: '24.57px',
    textDecoration: 'none',
  },
  callBtn: {
    display: 'flex',
    alignItems: 'center',
  },
  ladyImg: {
    width: 30,
    height: 30,
    objectFit: 'cover',
    borderRadius: 100,
    marginRight: 10,
  },
  text: {
    fontWeight: 600,
    fontSize: '1rem',
    marginLeft: theme.spacing(1),
  },
}));

const Topbar = (props: ToolbarProps): ReactElement => {
  const {
    onSidebarOpen,
    showMenu,
    showServiceWarranty,
    unClickable,
    isFleet,
    ...rest
  } = props;

  const history = useHistory();
  const dispatch = useDispatch();

  const loggedIn = useSelector((state: IReduxState) => state.auth.loggedIn);

  const { xsOnly } = useDeviseQuery();
  const classes = useStyles();

  const handleClickLogin = () => {
    if (loggedIn) history.push(URLS.DASHBOARD);
    else history.push(URLS.LOGIN);
  };

  const handleClickLogout = () => {
    dispatch(logout());
    window.location.href = URLS.LOGIN_FLEET;
  };

  return (
    <Box>
      <Hidden smDown>
        {showServiceWarranty && !isFleet && (
          <Box className={classes.contactBar}>
            <Container style={{ maxWidth: '100%', padding: '0px 120px' }}>
              <div className={classes.contactInfoContainer}>
                <Image
                  className={classes.contactImage1}
                  src={SvgSecurity}
                  alt="thefront"
                  lazy={false}
                />
                <div className={classes.text}>
                  Ask About Our Free Oil Change Membership
                </div>
                <div className={classes.flexGrow} />
                {!loggedIn && (
                  <Image
                    className={classes.contactImage2}
                    src={SvgPhone}
                    alt="thefront"
                    lazy={false}
                  />
                )}
                {!loggedIn && (
                  <Typography className="button">
                    {RETAIL_CONTACT_MESSAGE}
                  </Typography>
                )}
                {!loggedIn && (
                  <Image
                    className={classes.contactImage2}
                    src={SvgUser}
                    alt="thefront"
                    lazy={false}
                  />
                )}
                {!loggedIn && (
                  <button
                    type="button"
                    key="login"
                    className="button"
                    onClick={handleClickLogin}
                  >
                    Login
                  </button>
                )}
              </div>
            </Container>
          </Box>
        )}
      </Hidden>
      <Container style={{ maxWidth: '100%', padding: '0px 120px' }}>
        {showMenu && (
          <Hidden xsDown>
            <Toolbar disableGutters className={classes.toolbar} {...rest}>
              <div className={classes.logoContainer}>
                {!unClickable ? (
                  <a href="/" title="thefront">
                    <Image
                      className={clsx(
                        classes.logoImage,
                        xsOnly ? classes.imageCenterAlign : ''
                      )}
                      src={SvgLogo}
                      alt="thefront"
                      lazy={false}
                    />
                  </a>
                ) : (
                  <div title="thefront">
                    <Image
                      className={clsx(
                        classes.logoImage,
                        xsOnly ? classes.imageCenterAlign : ''
                      )}
                      src={SvgLogo}
                      alt="thefront"
                      lazy={false}
                    />
                  </div>
                )}
              </div>
              <div className={classes.flexGrow} />
              <div>
                {!loggedIn && (
                  <List className={classes.navigationContainer}>
                    <ListItem className={classes.listItem}>
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        className={classes.listItemText}
                        component="a"
                        href="https://goodhood.auto/services/"
                      >
                        Services
                      </Typography>
                    </ListItem>
                    <ListItem className={classes.listItem}>
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        className={classes.listItemText}
                        component="a"
                        href="https://goodhood.auto/faq/"
                      >
                        FAQ
                      </Typography>
                    </ListItem>
                    <ListItem className={classes.listItem}>
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        className={classes.listItemText}
                        component="a"
                        href="https://goodhood.auto/testimonials/"
                      >
                        Testimonials
                      </Typography>
                    </ListItem>
                  </List>
                )}
                {loggedIn && (
                  <IconButton className={classes.iconButton} aria-label="Menu">
                    <Typography className={classes.callBtn}>
                      {isFleet
                        ? 'Need Help? Please email fleet@goodhood.auto for any questions'
                        : RETAIL_CONTACT_MESSAGE}
                    </Typography>
                  </IconButton>
                )}
              </div>
              {!loggedIn && <div className={classes.flexGrow} />}
              <List className={classes.navigationContainer}>
                <ListItem className={classes.listItem}>
                  {!loggedIn && (
                    <ButtonForward
                      rounded
                      size="small"
                      className={classes.buttonQuote}
                    />
                  )}
                  {loggedIn && (
                    <ButtonLogout onClickHandler={handleClickLogout} />
                  )}
                </ListItem>
              </List>
            </Toolbar>
          </Hidden>
        )}
        <Hidden smUp>
          <Toolbar disableGutters className={classes.toolbar} {...rest}>
            <div className={classes.logoContainer}>
              {!unClickable ? (
                <a href="/" title="thefront">
                  <Image
                    className={clsx(
                      classes.logoImage,
                      xsOnly ? classes.imageCenterAlign : ''
                    )}
                    src={SvgLogo}
                    alt="thefront"
                    lazy={false}
                  />
                </a>
              ) : (
                <div title="thefront">
                  <Image
                    className={clsx(
                      classes.logoImage,
                      xsOnly ? classes.imageCenterAlign : ''
                    )}
                    src={SvgLogo}
                    alt="thefront"
                    lazy={false}
                  />
                </div>
              )}
            </div>
            <div className={classes.flexGrow} />
            <IconButton className={classes.iconButton} aria-label="Menu">
              <Typography className={classes.callBtn}>
                {RETAIL_CONTACT_MESSAGE}
              </Typography>
            </IconButton>
          </Toolbar>
        </Hidden>
      </Container>
    </Box>
  );
};

Topbar.defaultProps = {
  onSidebarOpen: undefined,
  showMenu: true,
  showServiceWarranty: true,
  unClickable: true,
  isFleet: false,
};

export default Topbar;
